import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import { Container, Reveal } from '../components/Reveal';
import { WhatsAppButton } from '../components/CTAs';
import { TESTIMONIALS } from '../data/testimonials';
import { breadcrumbSchema } from '../lib/schema';

export default function PatientStories() {
    const { t, lang } = useLang();
    return (
        <>
            <SEO
                title={t.stories.metaTitle}
                description={t.stories.metaDesc}
                path="/patient-stories"
                lang={lang}
                jsonLd={[breadcrumbSchema(lang, [{ name: t.nav.home, path: '/' }, { name: t.nav.stories, path: '/patient-stories' }])]}
            />
            <section className="relative overflow-hidden pt-28 md:pt-36" data-testid="patient-stories">
                <div className="pointer-events-none absolute -right-32 -top-20 h-96 w-96 rounded-full bg-peri-200/70" aria-hidden="true" />
                <Container className="relative">
                    <Reveal>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{t.nav.stories}</p>
                        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-tight tracking-tight text-ink md:text-6xl">{t.stories.title}</h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">{t.stories.sub}</p>
                    </Reveal>
                </Container>
            </section>

            <section className="py-16 md:py-24">
                <Container>
                    <Reveal>
                        <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.stories.themesTitle}</h2>
                    </Reveal>
                    <div className="mt-8 flex flex-wrap gap-2">
                        {t.home.remember.themes.map((th, i) => (
                            <Reveal key={th} delay={i * 0.04}>
                                <span className="inline-block rounded-full border border-peri-300 bg-peri-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-gold-700">{th}</span>
                            </Reveal>
                        ))}
                    </div>
                    <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {TESTIMONIALS.map((q, i) => (
                            <Reveal key={q.id} delay={i * 0.05}>
                                <div className="h-full rounded-3xl border border-peri-200 bg-white p-8" data-testid={`story-quote-${q.id}`}>
                                    <p className="font-serif text-lg italic leading-relaxed text-ink">{q.quote}</p>
                                    <div className="mt-6 flex items-center justify-between text-xs text-ink-subtle">
                                        <span className="inline-flex items-center gap-2 font-semibold text-gold-700">
                                            <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none"><path d="M3 8.5 6.5 12 13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            {t.stories.verifiedLabel}
                                        </span>
                                        <span>{q.lang.toUpperCase()}</span>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="bg-peri-50 py-16 md:py-24" data-testid="case-stories">
                <Container>
                    <Reveal>
                        <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.stories.caseTitle}</h2>
                        <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-ink-muted">{t.stories.caseNote}</p>
                    </Reveal>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        {[0, 1].map((i) => (
                            <Reveal key={i} delay={i * 0.07}>
                                <div className="rounded-3xl border border-dashed border-peri-400 bg-white p-8">
                                    <p className="font-serif text-lg italic text-ink-subtle">{t.stories.caseSlot}</p>
                                    <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-ink-subtle">
                                        <span>[PATIENT CONCERN]</span>
                                        <span>[OPTIONS DISCUSSED]</span>
                                        <span>[TREATMENT JOURNEY]</span>
                                        <span>[OUTCOME + CONSENT]</span>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-ink p-8 text-white md:p-10">
                            <p className="max-w-xl font-serif text-2xl font-semibold leading-snug">{t.home.nervous.title}</p>
                            <WhatsAppButton label={t.home.nervous.cta} source="stories" className="!bg-wa hover:!bg-wa-hover" />
                        </div>
                    </Reveal>
                </Container>
            </section>
        </>
    );
}
